import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

export const RelatedPosts = ({ posts, currentSlug }) => {
  // Filter out current post and limit to 3
  const relatedPosts = posts
    .filter(post => post.slug !== currentSlug)
    .slice(0, 3);

  if (relatedPosts.length === 0) return null;

  return (
    <div className="mt-12 pt-8 border-t border-stone-200" data-testid="related-posts">
      <h3 className="text-xl font-medium text-stone-900 mb-6" style={{fontFamily: "'Playfair Display', serif"}}>
        Related Posts
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {relatedPosts.map((post) => (
          <Link
            key={post.id}
            to={`/blog/${post.slug}`}
            className="group block"
          >
            <div className="aspect-[4/5] overflow-hidden mb-4">
              <img
                src={post.image_url}
                alt={post.title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </div>
            <p className="text-xs uppercase tracking-wider text-amber-800 mb-2">
              {post.category || 'Education & Advice'}
            </p>
            <h4 className="font-medium text-stone-900 group-hover:text-amber-800 transition-colors line-clamp-2">
              {post.title}
            </h4>
          </Link>
        ))}
      </div>
    </div>
  );
};

export const MostPopularPosts = ({ posts }) => {
  // Take first 4 as "most popular"
  const popularPosts = posts.slice(0, 4);

  return (
    <div className="bg-white border border-stone-200 p-6" data-testid="most-popular">
      <h4 className="text-sm uppercase tracking-wider text-stone-500 mb-4">Most Popular</h4>
      
      <div className="space-y-4">
        {popularPosts.map((post, index) => (
          <Link
            key={post.id}
            to={`/blog/${post.slug}`}
            className="flex gap-4 group"
          >
            <div className="w-20 h-20 flex-shrink-0 overflow-hidden">
              <img
                src={post.image_url}
                alt={post.title}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-amber-800 mb-1">{post.category || 'Guide'}</p>
              <h5 className="text-sm font-medium text-stone-900 group-hover:text-amber-800 transition-colors line-clamp-2">
                {post.title}
              </h5>
            </div>
          </Link>
        ))}
      </div>
      
      <Link 
        to="/blog"
        className="mt-4 flex items-center gap-1 text-sm text-amber-800 hover:text-amber-700"
      >
        View all posts <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
};

export const PostNavigation = ({ prevPost, nextPost }) => {
  return (
    <div className="flex justify-between items-center mt-12 pt-8 border-t border-stone-200" data-testid="post-navigation">
      {prevPost ? (
        <Link
          to={`/blog/${prevPost.slug}`}
          className="flex items-center gap-3 group max-w-[45%]"
        >
          <span className="text-2xl text-stone-300 group-hover:text-amber-800 transition-colors">←</span>
          <div>
            <p className="text-xs uppercase tracking-wider text-stone-500 mb-1">Previous</p>
            <p className="text-sm font-medium text-stone-900 group-hover:text-amber-800 transition-colors line-clamp-1">
              {prevPost.title}
            </p>
          </div>
        </Link>
      ) : (
        <div />
      )}
      
      {nextPost ? (
        <Link
          to={`/blog/${nextPost.slug}`}
          className="flex items-center gap-3 group max-w-[45%] text-right"
        >
          <div>
            <p className="text-xs uppercase tracking-wider text-stone-500 mb-1">Next</p>
            <p className="text-sm font-medium text-stone-900 group-hover:text-amber-800 transition-colors line-clamp-1">
              {nextPost.title}
            </p>
          </div>
          <span className="text-2xl text-stone-300 group-hover:text-amber-800 transition-colors">→</span>
        </Link>
      ) : (
        <div />
      )}
    </div>
  );
};

export const CategoryBadge = ({ category, className = '' }) => {
  const categoryColors = {
    'Education & Advice': 'bg-amber-100 text-amber-800',
    'Authentication': 'bg-blue-100 text-blue-800',
    'Buying Guide': 'bg-green-100 text-green-800',
    'Market Analysis': 'bg-purple-100 text-purple-800',
    'Designer Spotlight': 'bg-pink-100 text-pink-800',
    'default': 'bg-stone-100 text-stone-800',
  };

  const colorClass = categoryColors[category] || categoryColors.default;

  return (
    <span className={`inline-block text-xs uppercase tracking-wider px-3 py-1 ${colorClass} ${className}`}>
      {category || 'Guide'}
    </span>
  );
};

export default RelatedPosts;
